if (!customElements.get('carousel-slider')) {
  customElements.define(
    'carousel-slider',
    class CarouselSlider extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.carousel = this.querySelector('[class*="grid-carousel"]');
        this.initialize();
      }

      initialize() {
        const autoplayEnabled = this.getAttribute('data-autoplay') === 'true';
        const autoPlaySpeed = parseInt(this.getAttribute('data-speed'), 10) * 1000;
        const wrapAround = this.getAttribute('data-wrap-around') === 'true';

        // Build Swiper structure — replace grid classes with swiper equivalents
        const items = Array.from(this.carousel.querySelectorAll('.grid-item'));
        items.forEach((item) => {
          item.classList.remove('grid-item');
          item.classList.add('swiper-slide');
        });

        const wrapper = document.createElement('div');
        wrapper.classList.add('swiper-wrapper');
        items.forEach((item) => wrapper.appendChild(item));
        this.carousel.innerHTML = '';
        this.carousel.appendChild(wrapper);

        // Remove grid layout classes, keep only grid-carousel for CSS targeting
        ['grid', 'grid-1-col', 'grid-2-col-tablet', 'grid-3-col-desktop', 'grid-4-col-desktop'].forEach((cls) => {
          this.carousel.classList.remove(cls);
        });
        this.carousel.classList.add('swiper');

        const swiperConfig = {
          slidesPerView: 1.2,
          spaceBetween: 8,
          loop: wrapAround,
          grabCursor: true,
          slidesOffsetBefore: 0,
          slidesOffsetAfter: 0,
          breakpoints: {
            991: {
              slidesPerView: 'auto',
              slidesOffsetBefore: 60,
              slidesOffsetAfter: 60,
            },
          },
          autoplay: autoplayEnabled ? { delay: autoPlaySpeed, disableOnInteraction: false } : false,
          on: {
            slideChange: (swiper) => {
              const event = new CustomEvent('slider:slide-change', {
                detail: {
                  id: this.getAttribute('id'),
                  index: swiper.realIndex,
                },
              });
              document.dispatchEvent(event);
            },
          },
        };

        this.swiper = new Swiper(this.carousel, swiperConfig);
      }
    },
  );
}

if (!customElements.get('carousel-navigation')) {
  customElements.define(
    'carousel-navigation',
    class CarouselNavigation extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.init();
      }

      init(retry = 1, maxRetry = 3) {
        if (retry === maxRetry) {
          return;
        }

        const id = this.getAttribute('for');
        this.carouselComponent = document.querySelector(`#${id}`);
        this.wrap = this.dataset.wrap === 'true';

        if (!this.carouselComponent || !this.carouselComponent.swiper) {
          setTimeout(() => {
            this.init(retry + 1);
          }, 350);
          return;
        }

        this.swiper = this.carouselComponent.swiper;
        this.buttonPrev = this.querySelector('.js-prev');
        this.buttonNext = this.querySelector('.js-next');

        if (this.buttonPrev) {
          this.buttonPrev.addEventListener('click', (event) => {
            event.preventDefault();
            this.swiper.slidePrev();
          });
        }

        if (this.buttonNext) {
          this.buttonNext.addEventListener('click', (event) => {
            event.preventDefault();
            this.swiper.slideNext();
          });
        }

        const progressTrack = this.querySelector('.collection-slider-progress-track');

        const updateCollectionSliderProgress = () => {
          if (!progressTrack || !this.swiper) return;
          const swiper = this.swiper;
          const progress = Math.max(0, Math.min(1, swiper.progress));

          const visibleW = swiper.width || 0;
          const totalW = swiper.virtualSize || 0;
          const visibleFraction = totalW > 0 ? Math.min(1, visibleW / totalW) : 1;

          const fill = visibleFraction + (1 - visibleFraction) * progress;
          progressTrack.style.setProperty('--collection-slider-progress', String(fill));
        };

        const navShell = this.querySelector('.collection-slider-nav-shell');

        const updateNavButtons = () => {
          if (this.wrap) return;

          const swiper = this.swiper;
          const slides = Array.from(swiper.slides);
          const totalSlidesWidth = slides.reduce((sum, slide) => {
            const style = window.getComputedStyle(slide);
            return sum + slide.offsetWidth + parseInt(style.marginRight || '0', 10);
          }, 0);
          const allVisible = totalSlidesWidth <= swiper.size;

          if (navShell) {
            navShell.style.display = allVisible ? 'none' : '';
          }

          if (allVisible) {
            this.disableButton(this.buttonPrev);
            this.disableButton(this.buttonNext);
            return;
          }

          this.enableButton(this.buttonPrev);
          this.enableButton(this.buttonNext);

          if (swiper.isBeginning) {
            this.disableButton(this.buttonPrev);
          }
          if (swiper.isEnd) {
            this.disableButton(this.buttonNext);
          }
        };

        this.swiper.on('slideChange', updateNavButtons);
        this.swiper.on('resize', updateNavButtons);
        updateNavButtons();

        if (progressTrack) {
          this.swiper.on('progress', updateCollectionSliderProgress);
          this.swiper.on('slideChange', updateCollectionSliderProgress);
          this.swiper.on('resize', updateCollectionSliderProgress);
          updateCollectionSliderProgress();
        }
      }

      enableButton(button) {
        if (!button) return;
        button.classList.remove('pill-nav-item-disabled');
        button.classList.remove('focus-none');
        button.setAttribute('aria-disabled', 'false');
      }

      disableButton(button) {
        if (!button) return;
        button.classList.add('pill-nav-item-disabled');
        button.classList.add('focus-none');
        button.setAttribute('aria-disabled', 'true');
      }
    },
  );
}
